import { deleteField } from "firebase/firestore";
import { normalizeAllergenCodes } from "../../nutrition/foods/foodClassification";

const PROFILE_FIELD_MAPPINGS = [
  { settingsKey: "name_for_coach", propertyKey: "nameForCoach" },
  { settingsKey: "surname_for_coach", propertyKey: "surnameForCoach" },
  { settingsKey: "notes", propertyKey: "notes" },
  { settingsKey: "phone", propertyKey: "phone" },
  { settingsKey: "gender", propertyKey: "gender" },
  { settingsKey: "profile_squat", propertyKey: "profileSquat" },
  { settingsKey: "profile_bench_press", propertyKey: "profileBenchPress" },
  { settingsKey: "profile_deadlift", propertyKey: "profileDeadlift" },
  { settingsKey: "dietary_preference", propertyKey: "dietaryPreference" },
  {
    settingsKey: "allergen_codes",
    propertyKey: "allergenCodes",
    defaultValue: [],
    normalizeValue: normalizeAllergenCodes,
  },
  {
    settingsKey: "show_nutrition_plan_totals_override",
    propertyKey: "showNutritionPlanTotalsOverride",
    defaultValue: null,
  },
];

const isValidClient = (client) => !!client && typeof client === "object";

const cloneIfNeeded = (value) => (Array.isArray(value) ? [...value] : value);

const buildProfileSnapshot = (client, snapshotOverrides = {}) => {
  const snapshot = {};

  PROFILE_FIELD_MAPPINGS.forEach(
    ({ settingsKey, propertyKey, defaultValue, normalizeValue }) => {
      const hasOverride = Object.prototype.hasOwnProperty.call(
        snapshotOverrides,
        settingsKey
      );
      const sourceValue = hasOverride
        ? snapshotOverrides[settingsKey]
        : client?.[propertyKey];

      snapshot[settingsKey] = resolveComparableValue(
        sourceValue,
        defaultValue,
        normalizeValue
      );
    }
  );

  return snapshot;
};

const getClientProfileBaselineSnapshot = (client) =>
  buildProfileSnapshot(client, client?.clientSettingsBaseline ?? {});

const setClientProfileBaselineSnapshot = (client, snapshot) => {
  if (!isValidClient(client)) {
    return;
  }

  client.clientSettingsBaseline = buildProfileSnapshot(client, snapshot);
};

const resolveComparableValue = (value, defaultValue, normalizeValue) => {
  const resolvedValue =
    value === undefined || value === null ? defaultValue : value;

  if (typeof normalizeValue === "function") {
    return cloneIfNeeded(normalizeValue(resolvedValue));
  }

  return cloneIfNeeded(resolvedValue);
};

const areArraysEqual = (left, right) =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

const areDifferent = (left, right, defaultValue, normalizeValue) => {
  const normalizedLeft = resolveComparableValue(left, defaultValue, normalizeValue);
  const normalizedRight = resolveComparableValue(right, defaultValue, normalizeValue);

  if (Array.isArray(normalizedLeft) || Array.isArray(normalizedRight)) {
    return !areArraysEqual(
      Array.isArray(normalizedLeft) ? normalizedLeft : [],
      Array.isArray(normalizedRight) ? normalizedRight : []
    );
  }

  return normalizedLeft !== normalizedRight;
};

export const buildClientProfileSavePayload = (settings = {}) => {
  const payload = { ...settings };

  if (Object.prototype.hasOwnProperty.call(payload, "allergen_codes")) {
    payload.allergen_codes = normalizeAllergenCodes(payload.allergen_codes);
  }

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      "show_nutrition_plan_totals_override"
    ) &&
    (payload.show_nutrition_plan_totals_override === undefined ||
      payload.show_nutrition_plan_totals_override === null)
  ) {
    payload.show_nutrition_plan_totals_override = deleteField();
  }

  return payload;
};

export const syncClientProfileDraftWithPersistedData = (client) => {
  if (!isValidClient(client)) {
    return;
  }

  const persistedSnapshot = buildProfileSnapshot(client);

  PROFILE_FIELD_MAPPINGS.forEach(
    ({ settingsKey }) => {
      client.setSettings(
        settingsKey,
        cloneIfNeeded(persistedSnapshot[settingsKey])
      );
    }
  );

  setClientProfileBaselineSnapshot(client, persistedSnapshot);
};

export const syncUntouchedClientProfileDraftFields = (client) => {
  if (!isValidClient(client)) {
    return;
  }

  const settings = client.clientSettings ?? {};
  const persistedSnapshot = buildProfileSnapshot(client);
  const baselineSnapshot = getClientProfileBaselineSnapshot(client);
  const nextBaselineSnapshot = { ...baselineSnapshot };

  PROFILE_FIELD_MAPPINGS.forEach(({ settingsKey, defaultValue, normalizeValue }) => {
    const draftValue = resolveComparableValue(
      settings[settingsKey],
      defaultValue,
      normalizeValue
    );
    const baselineValue = baselineSnapshot[settingsKey];
    const persistedValue = persistedSnapshot[settingsKey];
    const isDirty = areDifferent(
      draftValue,
      baselineValue,
      defaultValue,
      normalizeValue
    );

    if (!isDirty) {
      client.setSettings(settingsKey, cloneIfNeeded(persistedValue));
      nextBaselineSnapshot[settingsKey] = cloneIfNeeded(persistedValue);
    }
  });

  setClientProfileBaselineSnapshot(client, nextBaselineSnapshot);
};

export const hasClientProfileUnsavedChanges = (client) => {
  if (!isValidClient(client)) {
    return false;
  }

  const settings = client.clientSettings ?? {};
  const baselineSnapshot = getClientProfileBaselineSnapshot(client);

  return PROFILE_FIELD_MAPPINGS.some(
    ({ settingsKey, propertyKey, defaultValue, normalizeValue }) =>
      areDifferent(
        settings[settingsKey],
        baselineSnapshot[settingsKey],
        defaultValue,
        normalizeValue
      )
  );
};

export const saveClientProfileDraft = (client) => {
  if (!isValidClient(client) || client.isOffline) {
    return false;
  }

  const settings = client.clientSettings ?? {};
  const baselineSnapshot = getClientProfileBaselineSnapshot(client);
  const changedSettings = {};
  let hasChanges = false;

  PROFILE_FIELD_MAPPINGS.forEach(
    ({ settingsKey, propertyKey, defaultValue, normalizeValue }) => {
      const nextValue = resolveComparableValue(
        settings[settingsKey],
        defaultValue,
        normalizeValue
      );

      if (
        areDifferent(
          nextValue,
          baselineSnapshot[settingsKey],
          defaultValue,
          normalizeValue
        )
      ) {
        client.setProperty(propertyKey, nextValue);
        client.setSettings(settingsKey, cloneIfNeeded(nextValue));
        changedSettings[settingsKey] = cloneIfNeeded(nextValue);
        hasChanges = true;
      }
    }
  );

  if (hasChanges) {
    client.saveClientProfile(buildClientProfileSavePayload(changedSettings));
    setClientProfileBaselineSnapshot(client, {
      ...baselineSnapshot,
      ...changedSettings,
    });
  }

  return hasChanges;
};

export const discardClientProfileDraft = (client) => {
  syncClientProfileDraftWithPersistedData(client);
};

export { PROFILE_FIELD_MAPPINGS };
